import { IHome } from "@src/components/Home";
import React, { useMemo, useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { useInView } from "react-intersection-observer";
import Image from "next/image";
import ClientOnly from "@src/components/ClientOnly";
import Floater from "../Floater";
import Footer from "../Footer";
import Vendors from "../Vendors";
import Parts from "../Parts";
import Backstory from "../Backstory";
import MarqueeComp from "../MarqueeComp";
import Quotes from "../Quotes";
import Masonry from "../Masonry";
import Hero from "../Hero";
import AsoEbi from "../AsoEbiLink";
import { useEventParts } from "@src/hooks/useEventParts";
import { useHome } from "@src/hooks/useHome";
import WeddingParty from "../WeddingParty";
import FAQ from "../FAQ";
import Recommendations from "../Recommendations";
import {
  Event_PreferencesQuery,
  Event_PreferencesQueryVariables,
} from "@src/@types/api.d";
import { GetEventPreferences } from "@src/graphql/queries";

const sectionNames = [
  "hero",
  "parts",
  "quotes",
  "backstory",
  "faq",
  "weddingParty",
  "masonry",
  "vendors",
  "recommendations",
];

const HomeT19: React.FC<IHome> = ({
  slug,
  eventBySlugData,
  eventId,
  eventPartsData,
  backStoryData,
  eventVendorData,
  albumDetailsData,
  heroImageData,
  hasWallet,
  getEventStreamersData,
  liveSchedules,
  eventUuid,
  weddingPartyData,
  faqData,
  recommendationData,
  inviteResult: initialInviteResult,
}) => {
  const { accessToken, inviteResult, getAsoebiData, isInvitedToEvent } =
    useHome({
      slug,
      eventBySlugData,
      eventId,
      eventPartsData,
      backStoryData,
      eventVendorData,
      albumDetailsData,
      heroImageData,
      hasWallet,
      getEventStreamersData,
      liveSchedules,
      eventUuid,
      weddingPartyData,
      inviteResult: initialInviteResult,
    });

  const { data: eventPreferencesData } = useQuery<
    Event_PreferencesQuery,
    Event_PreferencesQueryVariables
  >(GetEventPreferences, {
    variables: { eventId: eventUuid as string },
    skip: !eventUuid,
  });

  const shouldShowFaq = eventPreferencesData?.event_preferences?.faq ?? false;
  const shouldShowRecommendations =
    eventPreferencesData?.event_preferences?.recommendations ?? false;
  const { parts, hasEventParts } = useEventParts({
    eventPartsData,
    liveSchedules,
    eventTimezone: eventBySlugData?.eventBySlug?.eventTimezone,
    isInvitedToEvent,
  });

  const hasParts = hasEventParts;
  const hasBackstory =
    (backStoryData?.getBackStory?.filter((item) => item !== null).length ?? 0) >
    0;
  const hasFaq =
    shouldShowFaq &&
    (faqData?.getFAQsByEvent?.data?.filter(
      (item) =>
        item !== null && !!item.question && !!item.answer && item.is_active
    ).length ?? 0) > 0;
  const hasRecommendations =
    shouldShowRecommendations &&
    (recommendationData?.getEventRecommendationLists?.filter(Boolean).length ??
      0) > 0;
  const hasAsoEbi =
    isInvitedToEvent && (getAsoebiData?.getAseobi?.length ?? 0) > 0;
  const hasWeddingParty =
    (weddingPartyData?.getWeddingParty?.filter(
      (member) =>
        member !== null &&
        !!member.first_name &&
        !!member.last_name &&
        !!member.role
    ).length ?? 0) > 0;
  const hasMasonry = (albumDetailsData?.getMedia?.length ?? 0) > 0;
  const hasVendors = (eventVendorData?.eventVendor?.length ?? 0) > 0;

  // 1. Extract all valid images from the photobook
  const allImages = useMemo(() => {
    const media = albumDetailsData?.getMedia || [];
    return media
      .filter((img) => {
        if (!img || !img.meta) return false;
        // Exclude videos (type may be 'video' or similar)
        if (img.type && typeof img.type === "string") {
          const t = img.type.toLowerCase();
          if (t.includes("video")) return false;
        }
        const s3 = img.meta.s3_url;
        return (
          typeof s3 === "string" && s3.trim() !== "" && s3.startsWith("http")
        );
      })
      .map((img) => (img && img.meta ? img.meta.s3_url! : undefined))
      .filter((url): url is string => typeof url === "string");
  }, [albumDetailsData]);

  // 2. Assign images to sections sequentially, cycling if not enough images
  const sectionImages = useMemo(() => {
    const mapping: Record<string, string> = {};
    const fallbackImage =
      heroImageData.getHeroImage || "/images/generalDefaultImage.png";

    sectionNames.forEach((section, idx) => {
      if (allImages.length > 0) {
        // Assign images in order, cycling through if we run out
        mapping[section] = allImages[idx % allImages.length] || fallbackImage;
      } else {
        // If no images available, use hero image or general default as fallback
        mapping[section] = fallbackImage;
      }
    });
    return mapping;
  }, [allImages, heroImageData]);

  // 3. Section refs and inView hooks
  const [heroRef, heroInView] = useInView({ threshold: 0.5 });
  const [partsRef, partsInView] = useInView({ threshold: 0.5 });
  const [quotesRef, quotesInView] = useInView({ threshold: 0.5 });
  const [backstoryRef, backstoryInView] = useInView({ threshold: 0.5 });
  const [faqRef, faqInView] = useInView({ threshold: 0.5 });
  const [weddingPartyRef, weddingPartyInView] = useInView({ threshold: 0.5 });
  const [masonryRef, masonryInView] = useInView({ threshold: 0.5 });
  const [vendorsRef, vendorsInView] = useInView({ threshold: 0.5 });
  const [recommendationsRef, recommendationsInView] = useInView({
    threshold: 0.5,
  });

  // 4. Track which section is in view
  const [currentSection, setCurrentSection] = useState("hero");
  useEffect(() => {
    if (heroInView) setCurrentSection("hero");
    else if (hasParts && partsInView) setCurrentSection("parts");
    else if (quotesInView) setCurrentSection("quotes");
    else if (hasBackstory && backstoryInView) setCurrentSection("backstory");
    else if (hasFaq && faqInView) setCurrentSection("faq");
    else if (hasWeddingParty && weddingPartyInView)
      setCurrentSection("weddingParty");
    else if (hasMasonry && masonryInView) setCurrentSection("masonry");
    else if (hasVendors && vendorsInView) setCurrentSection("vendors");
    else if (hasRecommendations && recommendationsInView)
      setCurrentSection("recommendations");
  }, [
    heroInView,
    partsInView,
    quotesInView,
    backstoryInView,
    faqInView,
    weddingPartyInView,
    masonryInView,
    vendorsInView,
    recommendationsInView,
    hasParts,
    hasBackstory,
    hasFaq,
    hasWeddingParty,
    hasMasonry,
    hasVendors,
    hasRecommendations,
  ]);

  return (
    <>
      <ClientOnly>
        <Floater
          accessToken={accessToken}
          eventId={eventId}
          eventUuid={eventUuid}
          slugData={eventBySlugData}
          hasWallet={hasWallet}
          getEventStreamersData={getEventStreamersData}
          liveSchedules={liveSchedules}
          access_control={
            eventBySlugData?.eventBySlug?.event_preference?.access_control ??
            false
          }
          qr_code={inviteResult?.signupByInviteLink?.invitation?.qr_code ?? ""}
          getAsoebiData={getAsoebiData}
          admits={inviteResult?.signupByInviteLink?.invitation?.admits ?? 1}
          invitation={inviteResult?.signupByInviteLink?.invitation}
          isInvitedToEvent={isInvitedToEvent}
        />

        {/* Container for the split layout */}
        <div className="relative overflow-hidden flex flex-col lg:block">
          <div className="order-2 relative w-full h-[45vh] z-0 lg:order-none lg:fixed lg:left-0 lg:top-0 lg:w-1/2 lg:h-screen">
            <Image
              src={
                sectionImages[currentSection] ||
                heroImageData.getHeroImage ||
                ""
              }
              alt="section-image"
              layout="fill"
              className="object-cover object-center"
              priority
            />
          </div>

          {/* Scrollable Content Half */}
          <div className="order-1 w-full lg:w-1/2 lg:ml-[50%] relative z-10 min-h-screen lg:order-none">
            <section ref={heroRef}>
              <Hero
                heroImageData={heroImageData}
                eventId={eventId}
                slugData={eventBySlugData}
                sectionImage={sectionImages["hero"]}
              />
            </section>
            {hasParts && (
              <section ref={partsRef}>
                <Parts
                  liveSchedules={liveSchedules}
                  eventId={eventId}
                  parts={parts}
                  eventPartsData={eventPartsData}
                  eventTimezone={eventBySlugData?.eventBySlug?.eventTimezone}
                  sectionImage={sectionImages["parts"]}
                />
              </section>
            )}
            <section ref={quotesRef}>
              <Quotes sectionImage={sectionImages["quotes"]} />
            </section>
            {hasBackstory && (
              <section ref={backstoryRef}>
                <Backstory
                  eventId={eventId}
                  backStoryData={backStoryData}
                  sectionImage={sectionImages["backstory"]}
                />
              </section>
            )}

            {hasBackstory && hasFaq && (
              <div className="lg:hidden relative w-full h-[45vh]">
                <Image
                  src={
                    sectionImages["backstory"] ||
                    heroImageData.getHeroImage ||
                    ""
                  }
                  alt="backstory-section-image"
                  layout="fill"
                  className="object-cover object-center"
                />
              </div>
            )}

            {hasFaq && (
              <section ref={faqRef}>
                <FAQ
                  eventId={eventId}
                  faqData={faqData}
                  sectionImage={sectionImages["faq"]}
                />
              </section>
            )}

            {hasWeddingParty && (
              <section ref={weddingPartyRef}>
                <WeddingParty
                  eventId={eventId}
                  weddingPartyData={weddingPartyData}
                  sectionImage={sectionImages["weddingParty"]}
                />
              </section>
            )}

            {hasAsoEbi && (
              <section>
                <AsoEbi eventUuid={eventUuid} />
              </section>
            )}

            <section>
              <MarqueeComp eventId={eventId} slugData={eventBySlugData} />
            </section>

            {hasMasonry && (
              <section ref={masonryRef}>
                <Masonry
                  eventSlug={slug}
                  eventId={eventId}
                  albumDetailsData={albumDetailsData}
                  sectionImage={sectionImages["masonry"]}
                />
              </section>
            )}

            {hasVendors && (
              <section ref={vendorsRef}>
                <Vendors
                  eventId={eventId}
                  eventVendorData={eventVendorData}
                  sectionImage={sectionImages["vendors"]}
                />
              </section>
            )}
            {hasRecommendations && (
              <section ref={recommendationsRef}>
                <Recommendations
                  eventId={eventId}
                  recommendationData={recommendationData!}
                  sectionImage={sectionImages["recommendations"]}
                />
              </section>
            )}
          </div>
        </div>

        {/* Footer - Outside the split layout with higher z-index */}
        <div className="relative z-20">
          <Footer slug={eventBySlugData} />
        </div>
      </ClientOnly>
    </>
  );
};

export default HomeT19;
